import { MetricsDispatcher } from 'lib/MetricsDispatcher';
// @ts-ignore until we can do node run TS
import userAgent from 'lib/currentUserAgent';
// @ts-ignore - initial conversion to TS
import Config from 'Config';

export type MetricName =
  | 'app_crashed'
  | 'app_launched'
  | 'app_opened'
  // Composer Search.
  | 'composer.search.view.ctr.success'
  | 'composer.search.view.ctr.rejection'
  | 'composer.search.view.ctr.success.character_count'
  | 'composer.search.view.ctr.rejection.character_count'
  // Composer Composition.
  | 'composer.composition.view.sent.success'
  | 'composer.composition.view.sent.failed'
  // Stream Composer.
  | 'stream.chat.view.sent.success'
  | 'stream.chat.view.sent.failed'
  // Coaching Stream.
  | 'stream.coaching.view.sent.success'
  | 'stream.coaching.view.sent.failed'
  // Coaching signup funnel
  | 'coaching.signinfunnel.abtasty.loadtime'
  | 'coaching.signinfunnel.abtasty.timeout'
  | 'coaching.signinfunnel.loadtime';

export type MetricData = {
  type:
    | 'increment_count'
    | 'decrement_count'
    | 'timing_milliseconds'
    | 'gauge'
    | 'histogram'
    | 'unique_set';
  name: MetricName;
  // Ignored for incr/decr.
  value: number;
  // Only used for incr/decr if needing value > 1.
  countValue?: number;
  // Only used for unique set.
  setValue?: string;
  sampleRate?: number;
  tags: { [key: string]: string };
};

export const MetricTagKeys = [
  // Composer Search.
  'did_type_search_query',
  'is_urgent',
  // Composer Composition.
  'has_attachment',
  'is_scheduled',
  'has_plt_translations',
  'message_submission_type',
] as const;

export type MetricTagKey = typeof MetricTagKeys[number];

export type MetricTagValue =
  | 'true'
  | 'false'
  // Composer Composition.
  | 'new_scheduled_message'
  | 'update_scheduled_message'
  | 'group_chat'
  | 'new_message';

export const boolTagValue = (b: boolean): MetricTagValue => (b ? 'true' : 'false');

type OptionalParams = {
  tags?: { [key in MetricTagKey]?: MetricTagValue };
  sampleRate?: number;
};

export class MetricTracking {
  dispatcher: MetricsDispatcher;

  constructor() {
    this.dispatcher = new MetricsDispatcher();
    this.dispatcher.addSessionProperties({
      client_segment: userAgent.clientSegment(),
      client_version: Config.Environment.EMPIRE_RELEASE,
      os_name: userAgent.osDescription(),
    });
  }

  count(name: MetricName, value: number, optionalParams?: OptionalParams) {
    this.dispatcher.enqueue({
      type: 'increment_count',
      name,
      value: 0,
      countValue: value,
      sampleRate: optionalParams?.sampleRate,
      tags: (optionalParams?.tags as { [key: string]: string }) || {},
    });
  }

  increment(name: MetricName, optionalParams?: OptionalParams) {
    this.dispatcher.enqueue({
      type: 'increment_count',
      name,
      value: 0,
      sampleRate: optionalParams?.sampleRate,
      tags: (optionalParams?.tags as { [key: string]: string }) || {},
    });
  }

  decrement(name: MetricName, optionalParams?: OptionalParams) {
    this.dispatcher.enqueue({
      type: 'decrement_count',
      name,
      value: 0,
      sampleRate: optionalParams?.sampleRate,
      tags: (optionalParams?.tags as { [key: string]: string }) || {},
    });
  }

  timingMilliseconds(name: MetricName, value: number, optionalParams?: OptionalParams) {
    this.dispatcher.enqueue({
      type: 'timing_milliseconds',
      name,
      value,
      sampleRate: optionalParams?.sampleRate,
      tags: (optionalParams?.tags as { [key: string]: string }) || {},
    });
  }

  gauge(name: MetricName, value: number, optionalParams?: OptionalParams) {
    this.dispatcher.enqueue({
      type: 'gauge',
      name,
      value,
      sampleRate: optionalParams?.sampleRate,
      tags: (optionalParams?.tags as { [key: string]: string }) || {},
    });
  }

  histogram(name: MetricName, value: number, optionalParams?: OptionalParams) {
    this.dispatcher.enqueue({
      type: 'histogram',
      name,
      value,
      sampleRate: optionalParams?.sampleRate,
      tags: (optionalParams?.tags as { [key: string]: string }) || {},
    });
  }

  uniqueSet(name: MetricName, setValue: string, optionalParams?: OptionalParams) {
    this.dispatcher.enqueue({
      type: 'unique_set',
      name,
      value: 0,
      setValue,
      sampleRate: optionalParams?.sampleRate,
      tags: (optionalParams?.tags as { [key: string]: string }) || {},
    });
  }
}

export default new MetricTracking();
